import React, { FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { ReportConfigDto, ErrorResponse } from '@thingslog/repositories';
import { useToast } from '@thingslog/ui-components';
import Header from '../../components/header';
import TitleHeader from '../../components/TitleHeader/TitleHeader';
import { scheduledReportsQueryClient } from '../../clients/ReactQueryClients/ReactQueryClients';
import { ReportConfigForm } from './forms';
import useDetermineCompanyId from '../../hooks/useDetermineCompanyId';

const EditScheduledReportConfig: FC = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { reportName } = useParams<{ reportName: string }>();
  const selectedCompanyId = useDetermineCompanyId();

  const { useReportConfigFromReportName, useUpdateReportConfig } = useMemo(
    () => scheduledReportsQueryClient,
    []
  );

  const { data: reportConfig, isLoading: isLoadingConfig } = useReportConfigFromReportName(
    reportName || '',
    {
      enabled: !!reportName && selectedCompanyId !== null,
      onError: (error: AxiosError<ErrorResponse>) => {
        toast({
          type: 'error',
          message: error.response?.data?.message || t('report_config_fetch_error'),
          duration: 5000,
        });
        navigate('/app/ScheduledReportsConfig');
      },
    }
  );

  const { mutate: updateConfig, isLoading: isUpdating } = useUpdateReportConfig();

  const handleSubmit = (data: ReportConfigDto): void => {
    if (!reportName) return;

    updateConfig(
      { reportName, reportConfig: data },
      {
        onSuccess: () => {
          toast({
            type: 'success',
            message: t('report_config_update_success'),
            duration: 3000,
          });
          navigate('/app/ScheduledReportsConfig');
        },
        onError: (error: AxiosError<ErrorResponse>) => {
          toast({
            type: 'error',
            message: error.response?.data?.message || t('report_config_update_error'),
            duration: 5000,
          });
        },
      }
    );
  };

  const handleCancel = (): void => {
    navigate('/app/ScheduledReportsConfig');
  };

  if (selectedCompanyId === null) {
    return (
      <Header>
        <Container>
          <div className="text-2xl m-auto mt-20 text-center">
            {t('report_configs_select_company_message')}
          </div>
        </Container>
      </Header>
    );
  }

  if (isLoadingConfig) {
    return (
      <Header>
        <Container>
          <div className="flex justify-center p-8">
            <CircularProgress size={80} />
          </div>
        </Container>
      </Header>
    );
  }

  if (!reportConfig) {
    return (
      <Header>
        <Container>
          <div className="text-2xl m-auto mt-20 text-center">{t('report_config_not_found')}</div>
        </Container>
      </Header>
    );
  }

  return (
    <Header>
      <Container>
        <section className="flex justify-center mb-6">
          <TitleHeader title={t('report_config_edit_header')} />
        </section>
        <section className="max-w-2xl mx-auto">
          <ReportConfigForm
            defaultValues={reportConfig}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isEdit
            isSubmitting={isUpdating}
          />
        </section>
      </Container>
    </Header>
  );
};

export default EditScheduledReportConfig;
